// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Decisions — the unresolved report.
//
// This assembles what is *not yet settled* about an estimate onto one page,
// for the owner/architect meeting. It introduces no new data: every row here
// already exists in the model and is merely scattered across six tabs.
//
// Deliberately NOT a meeting-minutes tool. No notes field, no attendees, no
// action-item assignment, no distribution. Those make it generic project
// management software competing with tools that do it better; kept as a
// derived report it is something only this product can generate, because only
// this product carries line-level cost confidence.

import React, { useState, useMemo, useEffect, useRef } from "react";
import type { ScheduleDelta } from "../lib/scheduleDelta";
import { describeShift, scheduleUnchanged } from "../lib/scheduleDelta";
import type { Theme } from "../lib/theme";
import type { Division, ItemState } from "../types";
import type { AltTotals } from "../lib/calculations";
import { gatherDecisions } from "../lib/decisions";
import { STATUS_PALETTE } from "../lib/theme";
import * as Icon from "../components/OEIcons";
import { rangeIds, rangeStats } from "../lib/rangeSelect";
import SelectionStatus from "../components/SelectionStatus";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface DecisionsTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  divisions: Division[];
  itemState: Record<string, ItemState>;
  altTotals: AltTotals;
  rfis: any[];
  projectName: string;
  fmt: (n: number) => string;
  navigateToItem: (id: string) => void;
  /** Opens the print dialog on the Open Decisions sheet. */
  onPrint: () => void;
  setActiveTab: (tab: string) => void;
  /** Open one RFI on the RFI tab. Absent when the plan has no RFIs. */
  navigateToRfi?: (id: string) => void;
  /** Item count + total at the last snapshot, for the "since we last met" line. */
  lastSnapshot?: { label: string; date: string; total: number } | null;
  currentTotal: number;
  /** How the schedule differs from that snapshot -- duration, which scopes moved, peak month. */
  scheduleDelta?: ScheduleDelta | null;
}

export default function DecisionsTab({
  T, isMobile, isTablet, divisions, itemState, altTotals, rfis,
  projectName, fmt, navigateToItem, onPrint, setActiveTab, navigateToRfi, lastSnapshot, currentTotal, scheduleDelta,
}: DecisionsTabProps) {

  // ── Gather ───────────────────────────────────────────────────────────────
  // In lib/decisions, because the app also opens on this tab when a project
  // has anything outstanding, and a landing rule that counted them its own
  // way would eventually send somebody here to be told there is nothing.
  const { allowances, needsReview, altTBD, openRfis, count: decisionCount, exposure, altSwing } =
    gatherDecisions(divisions, itemState, altTotals, rfis);

  // ── Adding figures up ────────────────────────────────────────────────────
  // This page is read in a meeting, where the question is almost always "what
  // do those three come to?" -- asked once, answered, and dropped. So the
  // costs select and total like a spreadsheet's rather than the page growing
  // a calculator it has to keep.
  //
  // One list across all four sections, in the order they are drawn, so a drag
  // can run from the last alternate into the first item needing review. Rows
  // carrying no cost -- an RFI with only a schedule impact -- are in the
  // order but hold no figure, and rangeStats steps over them.
  const pickable = useMemo(() => [
    ...altTBD.map((a: any) => ({ id: a.id, amt: a.amt as number })),
    ...needsReview.map(r => ({ id: r.id, amt: r.amt as number })),
    ...allowances.map(r => ({ id: r.id, amt: r.amt as number })),
    ...openRfis.map((r: any) => ({ id: r.id, amt: (r.costImpact as number) || null })),
  ], [altTBD, needsReview, allowances, openRfis]);
  const order = useMemo(() => pickable.map(p => p.id), [pickable]);
  const amountOf = useMemo(() => {
    const m = new Map(pickable.map(p => [p.id, p.amt]));
    return (id: string) => m.get(id) ?? null;
  }, [pickable]);

  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [headId, setHeadId] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const dragging = useRef(false);

  const picked = useMemo(
    () => new Set(rangeIds(order, anchorId, headId, excluded)),
    [order, anchorId, headId, excluded]);
  const stats = useMemo(
    () => rangeStats([...picked], amountOf),
    [picked, amountOf]);

  const clearPick = () => { setAnchorId(null); setHeadId(null); setExcluded(new Set()); };

  // A drag ends wherever the button comes up, including off the last row or
  // outside the window entirely.
  useEffect(() => {
    const up = () => { dragging.current = false; };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") clearPick(); };
    window.addEventListener("mouseup", up);
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("mouseup", up); window.removeEventListener("keydown", esc); };
  }, []);

  /** Mousedown on a figure: start a run, extend one, or drop a row out of one. */
  const startPick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();   // the row beneath navigates; the figure selects
    if (e.ctrlKey || e.metaKey) {
      if (!anchorId) { setAnchorId(id); setHeadId(id); setExcluded(new Set()); return; }
      setExcluded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
      return;
    }
    if (e.shiftKey && anchorId) { setHeadId(id); return; }
    dragging.current = true;
    setAnchorId(id); setHeadId(id); setExcluded(new Set());
  };
  const extendPick = (id: string) => { if (dragging.current) setHeadId(id); };

  const snapDelta = lastSnapshot ? currentTotal - lastSnapshot.total : 0;

  // ── Shared bits ──────────────────────────────────────────────────────────
  const pad = isMobile ? "14px 16px" : isTablet ? "20px 24px" : "28px 36px";

  const Section = ({ title, hint, count, tone, children }: {
    title: string; hint: string; count: number; tone: string; children: React.ReactNode;
  }) => {
    if (count === 0) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
          <span style={{ width: 3, alignSelf: "stretch", background: tone, borderRadius: 999, flexShrink: 0 }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>{title}</div>
          <div style={{ fontSize: 10, color: tone, fontWeight: 700, letterSpacing: ".08em" }}>{count}</div>
        </div>
        <div style={{ fontSize: 11, color: T.textWeak, marginBottom: 8, paddingLeft: 13 }}>{hint}</div>
        <div style={{ paddingLeft: 13 }}>{children}</div>
      </div>
    );
  };

  /**
   * The division column shows a code, whatever the caller had.
   *
   * computeAltTotals carries div.name and gatherDecisions carries div.code, and
   * both land in the same column. Rather than make one of them lie by
   * truncation, a name is looked up and shown as the code its own section uses.
   */
  const divCode = (v: string | undefined): string | undefined => {
    if (!v) return undefined;
    if (/^Div\s/i.test(v)) return v;
    const hit = divisions.find((d: any) => d?.name === v);
    return hit?.code ?? v;
  };

  const Row = ({ onClick, left, mid, right, rightTone, rowId }: {
    onClick?: () => void; left: string; mid?: string; right?: string; rightTone?: string;
    /** Present when this row carries a figure that can be added up. */
    rowId?: string;
  }) => (
    <div onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px",
        borderBottom: `1px solid ${T.borderPrimary}40`, fontSize: 12,
        cursor: onClick ? "pointer" : "default" }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = T.bgHover; }}
      onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
      {/* One width and one kind of value. Alternates were passing the division
          *name* and every other section its *code*, into the same monospace
          column with a 46px floor -- so "Thermal & Moisture Protection" sat
          where "Div 32" sat, nothing lined up, and a label meant to be scanned
          past competed with the thing it labels. Fixed width, muted, truncated. */}
      {mid && <span style={{ fontSize: 10, color: T.textWeak, fontFamily: "monospace", flexShrink: 0,
        width: 58, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={mid}>{mid}</span>}
      <span style={{ flex: 1, color: T.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{left}</span>
      {/* The figure is the selection surface, not the row: clicking the
          description still opens the line item, exactly as clicking a cell in
          the estimate grid selects rather than following the row. */}
      {right && <span
        onMouseDown={rowId ? e => startPick(rowId, e) : undefined}
        onMouseEnter={rowId ? () => extendPick(rowId) : undefined}
        onClick={rowId ? e => e.stopPropagation() : undefined}
        title={rowId ? "Drag to add these up" : undefined}
        style={{ color: rightTone || T.textPrimary, fontWeight: 600, fontVariantNumeric: "tabular-nums",
          flexShrink: 0, padding: "1px 6px", margin: "-1px -6px", borderRadius: 3,
          cursor: rowId ? "cell" : undefined, userSelect: "none",
          background: rowId && picked.has(rowId) ? T.bgSelection : undefined,
          outline: rowId && picked.has(rowId) ? `1px solid ${T.borderAccent}55` : undefined,
          outlineOffset: -1 }}>{right}</span>}
    </div>
  );

  return (
    <div className="fade-in" onMouseDown={clearPick}
      style={{ padding: pad, maxWidth: 900, fontFamily: FONT }}>

      {/* The print sits with the title, not under the last row.
          This page is read in a meeting, so printing it is the first thing
          you do with it, not the last -- and at the bottom it was below
          however many open decisions the project happened to have, which is
          exactly the projects where you most want the paper. */}
      <div style={{ marginBottom: 6, display: "flex", alignItems: "flex-start", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.borderAccent }}>Decisions</div>
          <div style={{ fontSize: 12, color: T.textWeak }}>
            Everything still unresolved on {projectName || "this project"} — built for the next owner or architect meeting.
          </div>
        </div>
        {decisionCount > 0 && (
          <button onClick={() => window.print()} className="no-print"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none",
              border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, fontSize: 11, fontWeight: 600,
              padding: "6px 14px", minHeight: 24, borderRadius: 3, cursor: "pointer", fontFamily: FONT,
              flexShrink: 0, whiteSpace: "nowrap" }}>
            <Icon.Printer size={12}/> Print for the meeting
          </button>
        )}
      </div>

      {/* Headline: the confidence claim, itemised */}
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap", background: T.bgPanel,
        border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "16px 20px", margin: "16px 0 24px" }}>
        <div>
          <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 4 }}>Open decisions</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: decisionCount > 0 ? T.borderAccent : T.positive, lineHeight: 1 }}>{decisionCount}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 4 }}>Cost not yet firm</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmt(exposure)}</div>
        </div>
        {altTBD.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 4 }}>Alternates in play</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmt(altSwing)}</div>
          </div>
        )}
      </div>

      {/* Since we last met — the thing hardest to assemble by hand */}
      {lastSnapshot && (
        <div style={{ marginBottom: 24, padding: "10px 14px", background: T.bgAccentFaint,
          border: `1px solid ${T.borderPrimary}`, borderRadius: 6, fontSize: 12, color: T.textSecondary }}>
          <strong style={{ color: T.textPrimary }}>Since {lastSnapshot.label}</strong>
          <span style={{ color: T.textWeak }}> ({lastSnapshot.date}) — </span>
          {Math.abs(snapDelta) < 1
            ? <span>the total is unchanged.</span>
            : <>the total moved{" "}
                <span style={{ color: snapDelta > 0 ? T.negative : T.positive, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {snapDelta > 0 ? "+" : "−"}{fmt(Math.abs(snapDelta))}
                </span>
                <span> to {fmt(currentTotal)}.</span>
              </>}
          <button onClick={() => setActiveTab("history")}
            style={{ marginLeft: 8, background: "none", border: "none", color: T.borderAccent, cursor: "pointer", fontSize: 11, fontFamily: FONT, padding: 0 }}>
            See what changed →
          </button>
          {scheduleDelta && (
            <div style={{ marginTop: 6, color: T.textSecondary }}>
              {!scheduleDelta.comparable ? (
                <span style={{ color: T.textWeak }}>That snapshot predates schedule tracking, so timing can't be compared yet — snapshots from now on will carry it.</span>
              ) : scheduleUnchanged(scheduleDelta) ? (
                <span>The schedule is unchanged.</span>
              ) : (
                <>
                  {scheduleDelta.durationFrom !== scheduleDelta.durationTo && (
                    <span>Duration <strong style={{ color: T.textPrimary }}>{scheduleDelta.durationFrom} → {scheduleDelta.durationTo} mo</strong>. </span>
                  )}
                  {scheduleDelta.startFrom !== scheduleDelta.startTo && scheduleDelta.startTo && (
                    <span>Start moved to <strong style={{ color: T.textPrimary }}>{scheduleDelta.startTo}</strong>. </span>
                  )}
                  {scheduleDelta.moved.length > 0 ? (
                    <span>
                      <strong style={{ color: T.textPrimary }}>{scheduleDelta.moved.length} scope{scheduleDelta.moved.length === 1 ? "" : "s"} moved</strong>
                      {" — "}
                      {scheduleDelta.moved.slice(0, 3).map((mv, i) => (
                        <span key={mv.id}>
                          {i > 0 && ", "}
                          {mv.name} <span style={{ color: T.textWeak }}>({mv.divCode})</span> {describeShift(mv.startShiftDays || mv.endShiftDays)}
                        </span>
                      ))}
                      {scheduleDelta.moved.length > 3 && <span style={{ color: T.textWeak }}> and {scheduleDelta.moved.length - 3} more</span>}
                      {". "}
                    </span>
                  ) : (scheduleDelta.added || scheduleDelta.removed) ? null : <span>No scope moved. </span>}
                  {(scheduleDelta.added > 0 || scheduleDelta.removed > 0) && (
                    <span style={{ color: T.textWeak }}>
                      {scheduleDelta.added > 0 && `${scheduleDelta.added} newly scheduled`}
                      {scheduleDelta.added > 0 && scheduleDelta.removed > 0 && ", "}
                      {scheduleDelta.removed > 0 && `${scheduleDelta.removed} no longer scheduled`}
                      {". "}
                    </span>
                  )}
                  {scheduleDelta.peakFrom && scheduleDelta.peakTo && scheduleDelta.peakFrom !== scheduleDelta.peakTo && (
                    <span>Peak draw <strong style={{ color: T.textPrimary }}>{scheduleDelta.peakFrom} → {scheduleDelta.peakTo}</strong>.</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {decisionCount === 0 && (
        <div style={{ padding: "28px 20px", textAlign: "center", border: `1px dashed ${T.borderSecondary}`, borderRadius: 6 }}>
          <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 4 }}>Nothing outstanding</div>
          <div style={{ fontSize: 11, color: T.textWeak }}>
            No open alternates, allowances, flagged lines, open items, or RFIs. Every number in this estimate is defensible.
          </div>
        </div>
      )}

      <Section title="Alternates awaiting a decision" count={altTBD.length}
        tone={T.altStyle.Alt.leftBar}
        hint="Priced and ready — the owner needs to accept or reject each one.">
        {altTBD.map((a: any) => (
          <Row key={a.id} rowId={a.id} onClick={() => navigateToItem(a.id)} left={a.desc || "Untitled alternate"} mid={divCode(a.div)}
            right={`${a.amt >= 0 ? "+" : "−"}${fmt(Math.abs(a.amt))}`}
            rightTone={a.amt >= 0 ? T.negative : T.positive} />
        ))}
      </Section>

      <Section title="Needs review" count={needsReview.length}
        tone={STATUS_PALETTE["Needs Review"].hue}
        hint="Flagged as uncertain. These carry no confidence in the score until resolved.">
        {needsReview.map(r => (
          <Row key={r.id} rowId={r.id} onClick={() => navigateToItem(r.id)} left={r.desc} mid={r.div} right={fmt(r.amt)} />
        ))}
      </Section>

      <Section title="Allowances" count={allowances.length}
        tone={STATUS_PALETTE.Allowance.hue}
        hint="Placeholder budgets standing in for a real number. Each is a scope conversation.">
        {allowances.map(r => (
          <Row key={r.id} rowId={r.id} onClick={() => navigateToItem(r.id)} left={r.desc} mid={r.div} right={fmt(r.amt)} />
        ))}
      </Section>

      <Section title="Open RFIs" count={openRfis.length}
        tone={T.borderAccent}
        hint="Awaiting an answer. Cost and schedule impact shown where recorded.">
        {openRfis.map((r: any) => (
          <Row key={r.id} rowId={r.costImpact ? r.id : undefined} onClick={navigateToRfi ? () => navigateToRfi(r.id) : undefined}
            left={r.subject || "Untitled RFI"} mid={r.number ? `#${r.number}` : undefined}
            right={r.costImpact ? fmt(r.costImpact) : r.scheduleDays ? `${r.scheduleDays}d` : undefined}
            rightTone={r.costImpact ? T.negative : T.textMuted} />
        ))}
      </Section>

      {decisionCount > 0 && (
        <div style={{ marginTop: 8, paddingTop: 14, borderTop: `1px solid ${T.borderPrimary}` }}>
          <span style={{ fontSize: 10, color: T.textWeak }}>
            {navigateToRfi
              ? "Every row opens the thing it refers to — estimate lines in the estimate, RFIs on the RFI tab."
              : "Every estimate row opens its line in the estimate."}
          </span>
        </div>
      )}

      {/* The same readout the estimate grid puts in the same corner. */}
      <SelectionStatus T={T} count={stats.count} value={fmt(stats.sum)}
        average={stats.average == null ? null : fmt(stats.average)}/>
    </div>
  );
}
